import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { companyLegalFixtures } from '../fixtures/tenantSettingsFixtures';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { companyLegalKey, normalizeCompanyLegalProfile } from './companyLegal';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type { CompanyLegalProfile } from './types';
import {
  createLazyResourceStore,
  createWorkspaceStateStore,
  useLazyResource,
  useWorkspaceState,
} from './workspaceStore';
export type TenantSettingsCompanyWorkspace = {
  resource: AsyncResource<CompanyLegalProfile[]>;
  companies: CompanyLegalProfile[];
  ready: boolean;
  draft: CompanyLegalProfile | undefined;
  baseline: CompanyLegalProfile | undefined;
  dirty: boolean;
  setDraft: Dispatch<SetStateAction<CompanyLegalProfile | undefined>>;
  setBaseline: Dispatch<SetStateAction<CompanyLegalProfile | undefined>>;
  replace: (profile: CompanyLegalProfile) => void;
  add: (profile: CompanyLegalProfile) => void;
  setSealConfigured: (companyId: string, configured: boolean) => void;
};
type CompanyWorkspaceState = {
  companies: CompanyLegalProfile[];
  draft: CompanyLegalProfile | undefined;
  baseline: CompanyLegalProfile | undefined;
};
type CompanyWorkspaceStore = {
  resource: ReturnType<typeof createLazyResourceStore<CompanyLegalProfile[]>>;
  state: ReturnType<typeof createWorkspaceStateStore<CompanyWorkspaceState>>;
  synchronizedData: CompanyLegalProfile[] | undefined;
};
const companyWorkspaceStores = new WeakMap<TenantSettingsPort, CompanyWorkspaceStore>();
function companyWorkspaceStore(port: TenantSettingsPort) {
  let store = companyWorkspaceStores.get(port);
  if (!store) {
    store = {
      resource: createLazyResourceStore<CompanyLegalProfile[]>(),
      state: createWorkspaceStateStore<CompanyWorkspaceState>({
        companies: [],
        draft: undefined,
        baseline: undefined,
      }),
      synchronizedData: undefined,
    };
    companyWorkspaceStores.set(port, store);
  }
  return store;
}
export function useCompanyWorkspace(port: TenantSettingsPort, fixtureMode: boolean): TenantSettingsCompanyWorkspace {
  const store = companyWorkspaceStore(port);
  const resource = useLazyResource(
    store.resource,
    () => (fixtureMode ? Promise.resolve(companyLegalFixtures) : port.listCompanyLegalProfiles()),
    [fixtureMode, port],
  );
  const { companies, draft, baseline } = useWorkspaceState(store.state);
  useEffect(() => {
    if (!resource.data || store.synchronizedData === resource.data) return;
    store.synchronizedData = resource.data;
    store.state.update((current) => ({
      ...current,
      companies: resource.data!.map(normalizeCompanyLegalProfile),
    }));
  }, [resource.data, store]);
  const setDraft: Dispatch<SetStateAction<CompanyLegalProfile | undefined>> = (update) =>
    store.state.update((current) => ({
      ...current,
      draft: typeof update === 'function' ? update(current.draft) : update,
    }));
  const setBaseline: Dispatch<SetStateAction<CompanyLegalProfile | undefined>> = (update) =>
    store.state.update((current) => ({
      ...current,
      baseline: typeof update === 'function' ? update(current.baseline) : update,
    }));
  const replace = (profile: CompanyLegalProfile) =>
    store.state.update((current) => ({
      ...current,
      companies: current.companies.map((item) => (item.companyId === profile.companyId ? profile : item)),
    }));
  const add = (profile: CompanyLegalProfile) =>
    store.state.update((current) => ({
      ...current,
      companies: [...current.companies, profile].sort((left, right) =>
        left.descriptor.localeCompare(right.descriptor, 'ko-KR'),
      ),
    }));
  const setSealConfigured = (companyId: string, configured: boolean) => {
    store.state.update((current) => ({
      companies: current.companies.map((item) =>
        item.companyId === companyId
          ? {
              ...item,
              sealConfigured: configured,
            }
          : item,
      ),
      draft:
        current.draft?.companyId === companyId
          ? {
              ...current.draft,
              sealConfigured: configured,
            }
          : current.draft,
      baseline:
        current.baseline?.companyId === companyId
          ? {
              ...current.baseline,
              sealConfigured: configured,
            }
          : current.baseline,
    }));
  };
  const ready =
    resource.data !== undefined &&
    companies.length === resource.data.length &&
    companies.every((profile, index) => profile.companyId === resource.data?.[index]?.companyId);
  return {
    resource,
    companies,
    ready,
    draft,
    baseline,
    dirty: companyLegalKey(draft) !== companyLegalKey(baseline),
    setDraft,
    setBaseline,
    replace,
    add,
    setSealConfigured,
  };
}
